import {StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {useTimeout} from "@khanacademy/wonder-blocks-timing";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {usePublishAnalyticsEvent} from "@khan/analytics-learning-time";
import {
    getExperimentFeatureValue,
    getPromptVariable,
} from "@khan/guide-core-helpers";
import {useGuideState} from "@khan/guide-state";
import type {Message} from "@khan/guide-types";

import {containerDimensions} from "./card-sizing.ts";
import CardStack from "./card-stack.tsx";
import {ANIMATION_DURATION} from "./constants.ts";
import ControlsCard from "./controls-card.tsx";
import type {BlooketQuestion, DeckAnimation} from "./types.ts";

type Action =
    | {type: "EDIT_TOP_CARD"; card: BlooketQuestion}
    | {type: "ACCEPT_TOP_CARD"}
    | {type: "DISCARD_TOP_CARD"}
    | {type: "GET_NEW_CARDS"; cards: BlooketQuestion[]}
    | {type: "END_ANIMATION"};

interface DeckState {
    deck: BlooketQuestion[];
    topCard: BlooketQuestion | null;
    isAnimating: boolean;
    animation: DeckAnimation;
    animateOut: BlooketQuestion | null;
}

function reducer(state: DeckState, action: Action): DeckState {
    switch (action.type) {
        case "ACCEPT_TOP_CARD":
            return {
                ...state,
                deck: state.deck.slice(1),
                topCard: state.deck.length > 1 ? state.deck[1] : null,
                isAnimating: true,
                animation: "ACCEPTING",
                animateOut: state.deck.length > 0 ? state.deck[0] : null,
            };
        case "DISCARD_TOP_CARD":
            return {
                ...state,
                deck: state.deck.slice(1),
                topCard: state.deck.length > 1 ? state.deck[1] : null,
                isAnimating: true,
                animation: "DISCARDING",
                animateOut: state.deck.length > 0 ? state.deck[0] : null,
            };
        case "EDIT_TOP_CARD":
            return {
                ...state,
                topCard: action.card,
            };
        case "GET_NEW_CARDS":
            return {
                ...state,
                deck: state.deck.concat(action.cards),
                topCard:
                    state.deck.length === 0 && action.cards.length > 0
                        ? action.cards[0]
                        : state.topCard,
            };
        case "END_ANIMATION":
            return {
                ...state,
                isAnimating: false,
                animation: "NONE",
            };
    }
}

function prepCedarEventGradeLevel(
    gradeLevel: string | null | undefined,
):
    | "UNKNOWN"
    | "GRADE_1"
    | "GRADE_2"
    | "GRADE_3"
    | "GRADE_4"
    | "GRADE_5"
    | "GRADE_6"
    | "GRADE_7"
    | "GRADE_8"
    | "GRADE_9"
    | "GRADE_10"
    | "GRADE_11"
    | "GRADE_12"
    | "COLLEGE" {
    switch (gradeLevel) {
        case "1st grade":
            return "GRADE_1";
        case "2nd grade":
            return "GRADE_2";
        case "3rd grade":
            return "GRADE_3";
        case "4th grade":
            return "GRADE_4";
        case "5th grade":
            return "GRADE_5";
        case "6th grade":
            return "GRADE_6";
        case "7th grade":
            return "GRADE_7";
        case "8th grade":
            return "GRADE_8";
        case "9th grade":
            return "GRADE_9";
        case "10th grade":
            return "GRADE_10";
        case "11th grade":
            return "GRADE_11";
        case "12th grade":
            return "GRADE_12";
        case "College":
            return "COLLEGE";
    }
    return "UNKNOWN";
}

function prepCedarEventSubject(
    subject: string | null | undefined,
):
    | "READING_LANGUAGE_ARTS"
    | "MATH"
    | "SCIENCE"
    | "SOCIAL_STUDIES_HISTORY"
    | "WORLD_LANGUAGES"
    | "OTHER" {
    switch (subject) {
        case "reading_language_arts":
            return "READING_LANGUAGE_ARTS";
        case "math":
            return "MATH";
        case "science":
            return "SCIENCE";
        case "social_studies_history":
            return "SOCIAL_STUDIES_HISTORY";
        case "world_languages":
            return "WORLD_LANGUAGES";
    }
    return "OTHER";
}

interface Props {
    init?: BlooketQuestion[];
    refillThreshold: number;
    refill: () => Promise<void>;
    parseResponseMessage: (messageText: string) => BlooketQuestion[];
    keep: (item: BlooketQuestion) => void;
    cardWidth: number;
    cardHeight: number;
}

export default function Deck(props: Props): React.ReactNode {
    const {
        init = [],
        refill,
        refillThreshold,
        keep,
        parseResponseMessage,
        cardWidth,
        cardHeight,
    } = props;

    // The following 2 refs are used to block requests for more questions when
    // one is already in progress.
    const awaitingInitialCards = React.useRef<boolean>(true);
    const awaitingAdditionalCards = React.useRef<boolean>(false);

    // This holds new incoming messages from the AI Guide conversation.
    // Holding this in a dedicated state variable helps us manage useEffect
    // functions by isolating dependencies.
    const [unprocessedIncomingMsg, setUnprocessedIncomingMsg] =
        React.useState<Message | null>(null);
    const conversation = useGuideState("conversation");
    const threadId = useGuideState("threadId");
    const publishAnalyticsEvent = usePublishAnalyticsEvent();

    // We need to limit the number of automatic retries. If we retry
    // interminably, we will be rate-limited.
    const retries = React.useRef<number>(2);

    // Using a reducer makes it possible to update both topCard and deck
    // in the same render.
    const [state, dispatch] = React.useReducer(reducer, {
        deck: init,
        topCard: init.length > 0 ? init[0] : null,
        isAnimating: false,
        animation: "NONE",
        animateOut: null,
    });

    const onTimeout = React.useCallback(() => {
        dispatch({type: "END_ANIMATION"});
    }, [dispatch]);
    const {isSet, set} = useTimeout(onTimeout, ANIMATION_DURATION);

    const rawFeatureValue = getExperimentFeatureValue(
        "tx-blooket-use-new-model",
    );
    const flagValue = rawFeatureValue
        ? String(rawFeatureValue).toUpperCase()
        : "UNKNOWN";
    // if experimentModel is not "O3-MINI" or "DEFAULT", set it to "UNKNOWN"
    const model =
        flagValue === "O3-MINI"
            ? "O3-MINI"
            : flagValue === "DEFAULT"
              ? "GPT-4O"
              : "UNKNOWN";

    React.useEffect(() => {
        // If we should be animating, but the timer is not set, then set it.
        if (state.isAnimating && !isSet) {
            set();
        }
    }, [state.isAnimating, isSet, set]);

    const setTopCard = (card: BlooketQuestion): void => {
        dispatch({type: "EDIT_TOP_CARD", card});
    };

    const keepTopCard = (): void => {
        if (state.topCard !== null) {
            keep(state.topCard);
            dispatch({type: "ACCEPT_TOP_CARD"});
            publishAnalyticsEvent({
                eventType: "AiGuideBlooketQuestionAccepted",
                eventSchemaVersion: 3,
                aiGuideThreadId: threadId ?? "",
                model: model,
                grade: prepCedarEventGradeLevel(
                    getPromptVariable("gradeLevel"),
                ),
                subject: prepCedarEventSubject(getPromptVariable("subject")),
            });
        }
    };

    const discardTopCard = (): void => {
        dispatch({type: "DISCARD_TOP_CARD"});
        publishAnalyticsEvent({
            eventType: "AiGuideBlooketQuestionRejected",
            eventSchemaVersion: 3,
            aiGuideThreadId: threadId ?? "",
            model: model,
            grade: prepCedarEventGradeLevel(getPromptVariable("gradeLevel")),
            subject: prepCedarEventSubject(getPromptVariable("subject")),
        });
    };

    // This function requests more draft questions from the AI.
    // It will not fire a new request if one is already in progress.
    // As the response streams back, the AI Guide conversation will update.
    // The param `errorPresent` should be `true` if a refill is being
    // triggered on account of an error in the response from the LLM.
    // Note that `refill` is an async function.
    const maybeRefill = React.useCallback(
        (errorPresent: boolean) => {
            // Only attempt to refill if we are not already awaiting a response
            // from the LLM and have not used up all the automatic retries.
            if (
                !awaitingInitialCards.current &&
                !awaitingAdditionalCards.current &&
                retries.current > 0
            ) {
                awaitingAdditionalCards.current = true;

                if (errorPresent) {
                    retries.current = retries.current - 1;
                }

                refill().finally(() => {
                    awaitingAdditionalCards.current = false;
                });
            }
        },
        [refill],
    );

    // This will execute whenever the state of the AI Guide conversation
    // changes.  It sets unproccessedIncomingMessage to the latest completed
    // incoming message.
    React.useEffect(() => {
        const lastMessage =
            conversation.length === 0
                ? null
                : conversation[conversation.length - 1];

        if (
            lastMessage &&
            lastMessage.type === "incoming" &&
            !lastMessage?.isGeneratingResponse
        ) {
            setUnprocessedIncomingMsg(lastMessage);
        }
    }, [conversation]);

    // This will execute when there is a new and complete incoming message
    // from AI Guide.
    React.useEffect(() => {
        // If there is an unproccessed incoming message, try to parse it as a
        // JSON Blooket quiz and add the new questions to drafts.
        // If we are unable to parse the last message into valid Blooket
        // questions, it will retry.
        if (unprocessedIncomingMsg != null) {
            const lastMessage = unprocessedIncomingMsg;
            setUnprocessedIncomingMsg(null);

            // If this is the initial set of drafts, set the ref.
            // We should do this even if we are unable to parse the response.
            // Otherwise, it will not retry.
            if (awaitingInitialCards.current) {
                awaitingInitialCards.current = false;
            }

            // If the last message is an error, or there is no message text,
            // then retry.
            if (lastMessage.isError || !lastMessage.text) {
                maybeRefill(true);
                return;
            }

            // Parse the repsonse.
            const newQuestions = parseResponseMessage(
                unprocessedIncomingMsg.text,
            );

            if (newQuestions.length) {
                // New questions must be added to the end of the draft array.
                // Otherwise the draft being shown the user could change.
                dispatch({type: "GET_NEW_CARDS", cards: newQuestions});
                // Since this was a successful response, we can reset retries.
                retries.current = 2;
            } else {
                // If no valid questions could be parsed from the response,
                // then retry.
                maybeRefill(true);
            }
        }
    }, [unprocessedIncomingMsg, maybeRefill, parseResponseMessage]);

    // This will execute when the stack set changes size.
    // If the stack is below the refill threshold, then try to get more.
    React.useEffect(() => {
        if (state.deck.length < refillThreshold) {
            maybeRefill(false);
        }
    }, [state.deck, maybeRefill, refillThreshold]);

    const deckDimensions = containerDimensions(cardWidth, cardHeight);
    return (
        <View style={[deckDimensions, styles.deck]}>
            <CardStack
                activeCard={state.topCard}
                controlsCard={
                    <ControlsCard
                        cardHeight={cardHeight}
                        cardWidth={cardWidth}
                        discardTop={discardTopCard}
                        keepTop={keepTopCard}
                        disabled={state.isAnimating}
                    />
                }
                setCard={setTopCard}
                cardHeight={cardHeight}
                cardWidth={cardWidth}
                animation={state.animation}
                animateOut={state.animateOut}
                isAnimating={state.isAnimating}
            />
        </View>
    );
}

const styles = StyleSheet.create({
    deck: {
        position: "sticky",
        display: "block",
        top: 0,
        insetInlineStart: 0,
        inset: spacing.medium_16,
    },
});
